import React from "react"
import { useAppDispatch, useAppSelector } from "src/common/redux/hooks";
import { closeAddressModal, i18n, RootState, useForm } from "src/common";
import { Button, FieldConfig, Flex, Form, FormConfig, InputNumber, InputText, Text, Validation } from "src/components";
import styled from "@emotion/styled";
import IconModalClose from "../icons/closeModal.icon";

const ModalComponent = styled.div({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 100,
  backgroundColor: 'rgba(50, 50, 50, 0.55)',
  transition: 'all 0.5s ease-in-out'
});

const ModalContent = styled.div({
  boxSizing: 'border-box',
  backgroundColor: '#FFF',
  borderRadius: '16px',
  maxHeight: '92%',
  width: 'calc(100% - 40px)',
  maxWidth: '340px',
  position: 'fixed',
  top: '50%',
  left: '50%',
  right: 'auto',
  bottom: 'auto',
  transform: 'translate(-50%, -50%)',
  zIndex: 2,
  padding: '24px 12px',
  overflow: 'auto',
  boxShadow: '0 4px 40px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.5s ease-in-out'
});


const CloseIcon = styled(IconModalClose)({
  position: 'absolute',
  top: '24px',
  right: '12px',
});

interface AddressModalProps {
  onAddressModalSubmit: (values: any, type: 'insuredPerson' | 'contractor' | 'delivery') => void;
}

export const AddressModal = ({ onAddressModalSubmit }: AddressModalProps) => {
  const { formRef, submitForm } = useForm();
  const dispatch = useAppDispatch();
  const addressModal = useAppSelector((store: RootState) => store.state.modal.addressModal);

  const handleClose = () => {
    dispatch(closeAddressModal());
  }

  const handleSubmit = (values: any) => {
    const data = {
      city: values.c,
      zipCode: Number(values.p),
      street: values.s,
      houseNumber: values.h
    }
    onAddressModalSubmit(data, addressModal.type || 'insuredPerson');
  }

  const fields: FieldConfig[] = [
    {
      name: 'c',
      label: i18n.t('components.modals.addressModal.city'),
      component: InputText,
      validations: [
        { type: 'required', message: 'Potreban unos' },
      ] as Validation[],
    },
    {
      name: 'p',
      label: i18n.t('components.modals.addressModal.postalCode'),
      component: InputNumber,
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validPostalCode', message: 'Netočan unos. Unesi ispravni poštanski broj.' },
      ] as Validation[],
    },
    {
      name: 's',
      label: i18n.t('components.modals.addressModal.street'),
      component: InputText,
      validations: [
        { type: 'required', message: 'Potreban unos' },
      ] as Validation[],
    },
    {
      name: 'h',
      label: i18n.t('components.modals.addressModal.number'),
      component: InputText,
      componentProps: {
        maxLength: 5,
        autoComplete: "new-houseNumber-lvl2"
      },
      validations: [
        { type: 'required', message: 'Potreban unos' },
        { type: 'validHouseNumber', message: 'Netočan unos. Unesi ispravan kućni broj.' },
      ] as Validation[],
    }
  ]

  const formConfig: FormConfig = {
    gap: 24
  };

  if (!addressModal.open) {
    return null;
  }

  return (
    <ModalComponent>
      <ModalContent>
        <CloseIcon onClick={handleClose} />
        <Flex vertical gap={24}>
          <Text type="base" bold>{i18n.t('components.modals.addressModal.title')}</Text>
          <Form ref={formRef} fields={fields} config={formConfig} onSubmit={handleSubmit} />
          <Flex vertical gap={12}>
            <Button btnType="primary" onClick={() => submitForm()}>{i18n.t('components.modals.addressModal.save')}</Button>
            <Button btnType="textBlack" onClick={handleClose}>{i18n.t('components.modals.addressModal.close')}</Button>
          </Flex>
        </Flex>
      </ModalContent>
    </ModalComponent>
  )
}